import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, Gift, Coins, Flame, Clock, Trophy, Star,
  Calendar, Target, Loader2, Lock, Check
} from 'lucide-react';

const RewardsPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [timeStats, setTimeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [childRes, rewardsRes, timeRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get('/rewards'),
        api.get(`/time/stats/${childId}`)
      ]);
      setChild(childRes.data);
      setRewards(rewardsRes.data);
      setTimeStats(timeRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const canClaimReward = (reward) => {
    if (!child || !timeStats) return false;
    if (child.rewards?.includes(reward.id)) return false;
    
    switch (reward.reward_type) {
      case 'daily_streak':
        return (child.daily_streak || 0) >= reward.requirement_value;
      case 'time_spent':
        return (timeStats.total_time || 0) >= reward.requirement_value;
      case 'perfect_score':
        // Check via completed activities with 100% score
        return true; // Simplified - would need backend check
      default:
        return false;
    }
  };

  const getProgress = (reward) => {
    if (!child || !timeStats) return 0;
    
    switch (reward.reward_type) {
      case 'daily_streak':
        return Math.min(100, ((child.daily_streak || 0) / reward.requirement_value) * 100);
      case 'time_spent':
        return Math.min(100, ((timeStats.total_time || 0) / reward.requirement_value) * 100);
      default:
        return 0;
    }
  };

  const handleClaimReward = async (rewardId) => {
    setClaiming(rewardId);
    try {
      const response = await api.post(`/rewards/claim/${rewardId}?child_id=${childId}`);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      toast.success(`Claimed ${response.data.coins_earned} coins!`);
      fetchData(); // Refresh data
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to claim reward';
      toast.error(message);
    } finally {
      setClaiming(null);
    }
  };

  const rewardIcons = {
    'calendar': Calendar,
    'flame': Flame,
    'crown': Trophy,
    'clock': Clock,
    'star': Star,
    'trophy': Trophy,
  };

  const rewardColors = {
    'daily_login': { bg: 'bg-blue-100', icon: 'text-blue-500', border: 'border-blue-300' },
    'streak_7': { bg: 'bg-orange-100', icon: 'text-orange-500', border: 'border-orange-300' },
    'streak_30': { bg: 'bg-red-100', icon: 'text-red-500', border: 'border-red-300' },
    'time_30': { bg: 'bg-green-100', icon: 'text-green-500', border: 'border-green-300' },
    'time_60': { bg: 'bg-purple-100', icon: 'text-purple-500', border: 'border-purple-300' },
    'perfect_5': { bg: 'bg-yellow-100', icon: 'text-yellow-500', border: 'border-yellow-300' },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to={`/learn/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Gift className="w-6 h-6 text-amber-500" />
              <h1 className="font-fredoka font-bold text-xl text-slate-800">Rewards</h1>
            </div>
            <div className="flex items-center gap-2 bg-amber-100 rounded-full px-4 py-2">
              <Coins className="w-5 h-5 text-amber-600" />
              <span className="font-bold text-amber-700">{child?.coins || 0}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-orange-100 to-orange-50">
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{child?.daily_streak || 0}</p>
              <p className="text-sm text-slate-600">Day Streak</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-100 to-green-50">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{Math.round((timeStats?.total_time || 0) / 60)}m</p>
              <p className="text-sm text-slate-600">Total Time</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-100 to-purple-50">
            <CardContent className="p-4 text-center">
              <Star className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{child?.total_xp || 0}</p>
              <p className="text-sm text-slate-600">Total XP</p>
            </CardContent>
          </Card>
        </div>

        {/* Rewards Grid */}
        <h2 className="font-fredoka text-2xl font-bold text-slate-800 mb-6">
          Earn Coins!
        </h2>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rewards.map((reward, index) => {
            const IconComponent = rewardIcons[reward.icon] || Gift;
            const colors = rewardColors[reward.id] || { bg: 'bg-slate-100', icon: 'text-slate-500', border: 'border-slate-300' };
            const claimed = child?.rewards?.includes(reward.id);
            const canClaim = canClaimReward(reward);
            const progress = getProgress(reward);
            
            return (
              <motion.div
                key={reward.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`border-2 ${claimed ? 'border-green-300 bg-green-50' : colors.border}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                        {claimed ? (
                          <Check className="w-6 h-6 text-green-500" />
                        ) : (
                          <IconComponent className={`w-6 h-6 ${colors.icon}`} />
                        )}
                      </div>
                      <div className="flex items-center gap-1 bg-amber-100 rounded-full px-3 py-1">
                        <Coins className="w-4 h-4 text-amber-600" />
                        <span className="font-bold text-amber-700 text-sm">{reward.coins_value}</span>
                      </div>
                    </div>
                    
                    <h3 className="font-bold text-lg text-slate-800 mb-1">{reward.name}</h3>
                    <p className="text-sm text-slate-500 mb-3">{reward.description}</p>
                    
                    {!claimed && (
                      <div className="mb-3">
                        <Progress value={progress} className="h-2" />
                        <p className="text-xs text-slate-400 mt-1">{Math.round(progress)}% complete</p>
                      </div>
                    )}
                    
                    {claimed ? (
                      <Button disabled className="w-full bg-green-100 text-green-700">
                        <Check className="w-4 h-4 mr-2" /> Claimed!
                      </Button>
                    ) : canClaim ? (
                      <Button
                        className="w-full bg-amber-500 hover:bg-amber-600"
                        onClick={() => handleClaimReward(reward.id)}
                        disabled={claiming === reward.id}
                        data-testid={`claim-${reward.id}`}
                      >
                        {claiming === reward.id ? 'Claiming...' : 'Claim Reward!'}
                      </Button>
                    ) : (
                      <Button disabled className="w-full" variant="outline">
                        <Lock className="w-4 h-4 mr-2" /> Keep Going!
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default RewardsPage;
